/**
 * Funções utilitárias para gerenciamento de armazenamento local
 */

import { OrderObject, ProductRules, CONTRACTION_VARIABLE_FILE } from '../types/objects';
import { debugLog, logger } from '../../../lib/terminalLogger';
import { storage } from '../../../lib/storage/storage';
import { Keys } from '../../../lib/storage/keys';

interface SaveConfig {
  contraction: string;
  contractionDetails?: Array<{
    id: string;
    nome: string;
    contraction: string;
  }>;
  delivery: string;
  timestamp: string;
}

/**
 * Salva as configurações do pedido no localStorage
 * @param pedidoId ID do pedido
 * @param config Configurações a serem salvas
 */
export const savePedidoConfig = (pedidoId: number, config: SaveConfig): void => {
  try {
    const configKey = Keys.order.pedidoConfig(pedidoId);
    storage.set(configKey, config, { area: 'local' });
    debugLog('ACTION: save-pedido-config <- received', { 
      status: 'ok', 
      data: { 
        pedidoId, 
        configKey,
        contracao: config.contraction,
        entrega: config.delivery
      } 
    });
  } catch (error) {
    debugLog('ACTION: save-pedido-config <- received', { 
      status: 'error', 
      data: { 
        pedidoId, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      } 
    });
  }
};

/**
 * Salva as regras do produto no localStorage
 * @param pedidoId ID do pedido
 * @param itemId ID do item
 * @param rules Regras do produto
 */
export const saveProductRules = (pedidoId: number, itemId: number, rules: ProductRules): void => {
  try {
    // Salvar com nome específico
    const ruleKey = Keys.rules.productRules(pedidoId, itemId);
    storage.set(ruleKey, rules, { area: 'local' });
    debugLog('ACTION: save-product-rules <- received', { 
      status: 'ok', 
      data: { 
        pedidoId, 
        itemId, 
        ruleKey,
        nomeServico: rules.nome_servico
      } 
    });
    
    // Manter versão genérica para compatibilidade (legado)
    try { window.localStorage.setItem('productRules', JSON.stringify(rules)); } catch {}
  } catch (error) {
    debugLog('ACTION: save-product-rules <- received', { 
      status: 'error', 
      data: { 
        pedidoId, 
        itemId, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      } 
    });
  }
};

/**
 * Salva as preferências do usuário no localStorage
 * @param contraction Contração global
 * @param delivery Opção de entrega
 */
export const saveUserPreferences = (contraction: string, delivery: string): void => {
  try {
    storage.set(Keys.prefs.user, {
      contraction,
      delivery,
      lastUpdated: new Date().toISOString()
    }, { area: 'local' });
  } catch (error) {
    logger.error('❌ Erro ao salvar preferências:', error);
  }
};

/**
 * Prepara os dados do pedido para salvamento
 * @param params Parâmetros necessários para o salvamento
 * @returns Objeto com os dados formatados para salvamento
 */
export const prepareSaveData = (params: {
  pedidoId: number;
  itemId: number;
  nomeArquivo: string;
  cliente: { id?: number; nome: string; nome_fantasia: string };  // ⭐ Adicionado id para estrutura de pastas
  produto: { pro_id?: number; nome: string; tipo: string };
  material: string;
  productRules?: ProductRules;
  contraction: string;
  hasMultipleContractions: boolean;
  delivery: string;
  objects: OrderObject[];
  totalObjectsWithQuantity: number;
  totalAreaWithQuantity: number;
  metrosVendidos: number;
  usuario: string;  // ✅ NOVO: username/login para auditoria
  ostId?: number;  // ✅ NOVO: ost_id para movimentação de stage
  oitStatusId?: number;  // Status atual do item (oit_status_id) para evitar regressão
}) => {
  const {
    pedidoId,
    itemId,
    nomeArquivo,
    cliente,
    produto,
    material,
    productRules,
    contraction,
    hasMultipleContractions,
    delivery,
    objects,
    totalObjectsWithQuantity,
    totalAreaWithQuantity,
    metrosVendidos,
    usuario,  // ✅ NOVO: desestruturar
    ostId,  // ✅ NOVO: desestruturar ost_id
    oitStatusId  // Status atual do item
  } = params;

  return {
    pedido: {
      orc_id: pedidoId,
      oit_id: itemId,
      nome_arquivo: nomeArquivo,
      ...(ostId ? { ost_id: ostId } : {}), // ✅ Incluir ost_id se fornecido
      ...(oitStatusId ? { oit_status_id: oitStatusId } : {}) // Status atual do item
    },
    usuario,  // ✅ NOVO: incluir no retorno (username para auditoria)
    cliente,
    produto: {
      pro_id: typeof produto.pro_id === 'number' ? produto.pro_id : undefined,
      nome: produto.nome,
      tipo: produto.tipo
    },
    material: {
      nome: material
    },
    diretorios: {
      interno: productRules?.diretorio_base_interno || '',
      externo: productRules?.diretorio_base_externo || '',
      exportpng: productRules?.diretorio_base_exportpng || '',      // ← NOVO
      export_prod: productRules?.diretorio_base_export_prod || ''   // ← NOVO
    },
    configuracoes: {
      contracao: hasMultipleContractions ? CONTRACTION_VARIABLE_FILE : contraction,
      contracoesVariaveis: hasMultipleContractions,
      entrega: delivery
    },
    regras: {
      ...productRules,
      // Garantir que diretorio_nome seja preenchido com fallback
      diretorio_nome: productRules?.diretorio_nome || productRules?.diretorios?.nome || ''
    },
    objetos: objects,
    totalObjetos: totalObjectsWithQuantity,
    totalArea: totalAreaWithQuantity,
    metrosVendidos
  };
}; 